import { pickBy } from 'lodash-es';
import { DateTime } from 'luxon';

import type { PgTeam } from '@poseidon/types/pg-gen/team';
import type { PgUserTeamData } from '@poseidon/types/pg-gen/userTeamData';
import type { SvrContactPrimaryFields } from '@poseidon/types/svr/svrContact.type';
import type { SvrOrg } from '@poseidon/types/svr/svrOrg.type';
import type { AtVariableDict } from '@poseidon/types/types/atVariables.type';
import type { ContactPrimaryFields } from '@poseidon/types/types/contact.type';
import type { OrgPrimaryFields } from '@poseidon/types/types/org.type';
import { AtVariableKeys } from '@poseidon/types/types/atVariables.type';

import { isNotEmpty } from '.';
import { stripHtmlTags } from './html';

/**
 * Turn a template subject/body — HTML with `@`-variable mention spans (whose inner text is
 * already the `{Placeholder}`) plus any bare `{Placeholder}` text — into PLAIN TEXT for a
 * human to review and edit: strip the HTML, then fill in every `{Variable}` we CAN resolve
 * from `atVariables`, leaving the ones we can't as `{Variable}` placeholders for the rep to
 * complete.
 *
 * Used by the /todo/review screen so the rep edits readable text instead of raw mention
 * HTML. Send-time resolution is span-based and unaffected by this — it only shapes what the rep
 * sees, which they then edit and send as plain text. A leftover `{...}` still re-interrupts the
 * step (see `replacePlaceholders` in server-utils), so nothing unresolved is ever sent.
 */
export function fillResolvableVariables(html: string | null | undefined, atVariables: AtVariableDict): string {
  const text = stripHtmlTags(html);
  // Entries let us look a `{key}` (an arbitrary string from the text) up against the typed dict
  // without an unsafe cast.
  const entries = Object.entries(atVariables);
  const findVal = (k: string): string | undefined => entries.find(([ek]) => ek === k)?.[1];
  return text.replace(/\{([^{}[\]]+)\}/g, (match, rawKey: string) => {
    const key = rawKey.trim();
    // Mirror convertTemplateToHtml's `Name` → `FirstName` fallback.
    const value = key === 'Name' ? (findVal('FirstName') ?? findVal('Name')) : findVal(key);
    // Substitute only a concrete value — never another placeholder or an empty string.
    if (value != null && value.length > 0 && !(value.startsWith('{') && value.endsWith('}'))) return value;
    return match;
  });
}

export function extractAtVariablesV2(
  team: { name: PgTeam['name'] },
  userData: {
    name: PgUserTeamData['name'];
    title: PgUserTeamData['title'];
    calendar_link: PgUserTeamData['calendar_link'];
    contact_number: PgUserTeamData['contact_number'];
    email: PgUserTeamData['contact_email'];
  },
  contact_primary_fields: ContactPrimaryFields | null,
  org_primary_fields: OrgPrimaryFields | null,
  contact_about?: { liAbout?: string | null },
  myLatestPostUrl?: string | null,
): AtVariableDict {
  const data: AtVariableDict = {
    Today: DateTime.now().toFormat('EEEE'),
  };

  if (isNotEmpty(myLatestPostUrl)) {
    data.MyLatestPost = myLatestPostUrl;
  }

  const firstName = contact_primary_fields?.firstName?.data;
  if (isNotEmpty(firstName)) {
    data.FirstName = firstName;
  }

  const lastName = contact_primary_fields?.lastName?.data;
  if (isNotEmpty(lastName)) {
    data.LastName = lastName;
  }

  const fullName = firstName == null || lastName == null ? undefined : `${firstName} ${lastName}`;
  if (isNotEmpty(fullName)) {
    data.FullName = fullName;
  }

  const company = contact_primary_fields?.company?.data;
  if (isNotEmpty(company)) {
    data.Company = company;
  } else {
    // try looking at company full
    const companyFull = contact_primary_fields?.companyFull?.data;
    if (isNotEmpty(companyFull)) {
      data.Company = companyFull;
    }
  }

  const city = contact_primary_fields?.city?.data;
  if (isNotEmpty(city)) {
    data.City = city;
  }

  const title = contact_primary_fields?.title?.data;
  if (isNotEmpty(title)) {
    data.Title = title;
  }

  const headline = contact_primary_fields?.headline?.data;
  if (isNotEmpty(headline)) {
    data.Headline = headline;
  }

  const about = contact_about?.liAbout;
  if (isNotEmpty(about)) {
    data.About = about;
  }

  const teamName = team.name;
  if (isNotEmpty(teamName)) {
    data.MyTeam = teamName;
  }

  const myFirstName = userData.name?.split(' ')[0]?.trim();
  if (isNotEmpty(myFirstName)) {
    data.MyName = myFirstName;
    data.MyFirstName = myFirstName;
  }

  const myFullName = userData.name;
  if (isNotEmpty(myFullName)) {
    data.MyFullName = myFullName;
  }

  const myTitle = userData?.title;
  if (isNotEmpty(myTitle)) {
    data.MyTitle = myTitle;
  }

  const myCalendarLink = userData?.calendar_link;
  if (isNotEmpty(myCalendarLink)) {
    data.MyCalendarLink = myCalendarLink;
  }

  const myContactNumber = userData?.contact_number;
  if (isNotEmpty(myContactNumber)) {
    data.MyContactNumber = myContactNumber;
  }

  const myContactEmail = userData?.email;
  if (isNotEmpty(myContactEmail)) {
    data.MyContactEmail = myContactEmail;
  }

  if (org_primary_fields) {
    const companyNamePrimaryField = org_primary_fields?.name;
    const companyName = companyNamePrimaryField?.data;
    if (companyName != null) data.Company = companyName;
  }

  return data;
}

export function atVariablesPlaceholders() {
  const dict: AtVariableDict = {};

  AtVariableKeys.forEach((k) => {
    dict[k] = `{${k}}`;
  });
  return dict;
}

export function svrExtractAtVariables(
  userTeamData: {
    teamName: string;
    userTeamName: string | null;
    title: string | null;
    calendarLink: string | null;
    contactNumber: string | null;
    userTeamEmail: string | null;
    unipileAccountId?: string | null;
  },
  contact: { primaryFields: SvrContactPrimaryFields; liAbout?: string | null } | null,
  org: SvrOrg | null | undefined,
) {
  const firstName = contact?.primaryFields?.firstName?.data;
  const lastName = contact?.primaryFields?.lastName?.data;
  const fullName = firstName != null && lastName != null ? `${firstName} ${lastName}` : undefined;
  const company = contact?.primaryFields?.company?.data;
  const companyFull = contact?.primaryFields?.companyFull?.data;
  const city = contact?.primaryFields?.city?.data;
  const title = contact?.primaryFields?.title?.data;
  const headline = contact?.primaryFields?.headline?.data;
  const about = contact?.liAbout;

  const myFirstName = userTeamData.userTeamName?.split(' ')[0]?.trim();
  const myFullName = userTeamData.userTeamName;
  const myTitle = userTeamData?.title;
  const myCalendarLink = userTeamData?.calendarLink;
  const myContactNumber = userTeamData?.contactNumber;
  const myContactEmail = userTeamData?.userTeamEmail;
  const orgCompanyName = org?.fields?.name?.find((f) => f.primary)?.data;

  const data: AtVariableDict = {
    Today: DateTime.now().toFormat('EEEE'),
    MyTeam: isNotEmpty(userTeamData.teamName) ? userTeamData.teamName : undefined,
    FirstName: isNotEmpty(firstName) ? firstName : undefined,
    LastName: isNotEmpty(lastName) ? lastName : undefined,
    FullName: isNotEmpty(fullName) ? fullName : undefined,
    Company: isNotEmpty(orgCompanyName)
      ? orgCompanyName
      : isNotEmpty(company)
        ? company
        : isNotEmpty(companyFull)
          ? companyFull
          : undefined,
    City: isNotEmpty(city) ? city : undefined,
    Title: isNotEmpty(title) ? title : undefined,
    Headline: isNotEmpty(headline) ? headline : undefined,
    About: isNotEmpty(about) ? about : undefined,

    MyName: isNotEmpty(myFirstName) ? myFirstName : undefined,
    MyFirstName: isNotEmpty(myFirstName) ? myFirstName : undefined,
    MyFullName: isNotEmpty(myFullName) ? myFullName : undefined,
    MyTitle: isNotEmpty(myTitle) ? myTitle : undefined,
    MyCalendarLink: isNotEmpty(myCalendarLink) ? myCalendarLink : undefined,
    MyContactNumber: isNotEmpty(myContactNumber) ? myContactNumber : undefined,
    MyContactEmail: isNotEmpty(myContactEmail) ? myContactEmail : undefined,
    // Only offer @MyLatestPost when the team has Unipile connected — posts
    // parsing (and therefore the latest-post URL) is always on for connected
    // teams. The real URL is resolved server-side at send time; the editor shows
    // the placeholder, matching how unresolved contact variables appear before a
    // contact is loaded.
    MyLatestPost: isNotEmpty(userTeamData.unipileAccountId) ? '{MyLatestPost}' : undefined,
  };

  return pickBy(data, (v) => v !== undefined);
}
